import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Send, Save, AlertCircle, Settings2 } from 'lucide-react';
import api from '@/lib/api';
import { PollQuestionForm } from '../components';
import type { PollQuestion } from '../types';

export function CreatePollPage() {
  const questionCounterRef = useRef(0);

  const nextQuestionId = useCallback((): string => {
    return `q_${++questionCounterRef.current}`;
  }, []);

  const emptyQuestion = useCallback((): PollQuestion => {
    return {
      questionId: nextQuestionId(),
      text: '',
      type: 'single_choice',
      options: ['', ''],
      required: true,
    };
  }, [nextQuestionId]);
  const navigate = useNavigate();
  const { pollId } = useParams<{ pollId: string }>();
  const isEditing = Boolean(pollId);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [resultsVisibility, setResultsVisibility] = useState<'public' | 'creator_only'>('public');
  const [closesAt, setClosesAt] = useState('');
  const [questions, setQuestions] = useState<PollQuestion[]>([emptyQuestion()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingPoll, setLoadingPoll] = useState(isEditing);

  useEffect(() => {
    if (!pollId) return;

    async function loadPoll() {
      try {
        const poll = await api.getPoll(pollId!);
        setTitle(poll.title);
        setDescription(poll.description || '');
        setResultsVisibility(poll.resultsVisibility as 'public' | 'creator_only');
        setClosesAt(poll.closesAt ? poll.closesAt.slice(0, 16) : '');
        setQuestions(poll.questions as PollQuestion[]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load poll');
      } finally {
        setLoadingPoll(false);
      }
    }

    loadPoll();
  }, [pollId]);

  const handleSave = async (publish: boolean) => {
    setSaving(true);
    setError(null);

    try {
      const input = {
        title: title.trim(),
        description: description.trim() || undefined,
        questions,
        resultsVisibility,
        closesAt: closesAt ? new Date(closesAt).toISOString() : undefined,
      };

      let savedPollId: string;

      if (isEditing && pollId) {
        const updated = await api.updatePoll(pollId, input);
        savedPollId = updated.pollId;
      } else {
        const created = await api.createPoll(input);
        savedPollId = created.pollId;
      }

      if (publish) {
        await api.publishPoll(savedPollId);
      }

      navigate(`/a/arche_polls/${savedPollId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save poll');
    } finally {
      setSaving(false);
    }
  };

  if (loadingPoll) {
    return (
      <div className="max-w-3xl mx-auto space-y-4 pt-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pt-6">
      <h1 className="text-2xl font-bold mb-6">{isEditing ? 'Edit Poll' : 'Create Poll'}</h1>

      {error && (
        <div className="mb-6 p-3 bg-destructive/10 text-destructive rounded-lg text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="space-y-8">
        {/* Title & Description */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="title" className="mb-1.5">
              Title
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What is your poll about?"
              className="text-base"
            />
          </div>

          <div>
            <Label htmlFor="description" className="mb-1.5">
              Description (optional)
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide more context about your poll..."
              className="min-h-[80px]"
            />
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">Questions</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQuestions([...questions, emptyQuestion()])}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Question
            </Button>
          </div>

          {questions.map((q, i) => (
            <PollQuestionForm
              key={q.questionId}
              question={q}
              index={i}
              onChange={(updated) => {
                const newQuestions = [...questions];
                newQuestions[i] = updated;
                setQuestions(newQuestions);
              }}
              onRemove={() => {
                if (questions.length > 1) {
                  setQuestions(questions.filter((_, j) => j !== i));
                }
              }}
            />
          ))}
        </div>

        {/* Settings */}
        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center gap-2 mb-4">
              <Settings2 className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-semibold">Settings</Label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="visibility" className="text-xs text-muted-foreground mb-1.5">
                  Results Visibility
                </Label>
                <select
                  id="visibility"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={resultsVisibility}
                  onChange={(e) =>
                    setResultsVisibility(e.target.value as 'public' | 'creator_only')
                  }
                >
                  <option value="public">Public (visible to respondents)</option>
                  <option value="creator_only">Creator Only</option>
                </select>
              </div>

              <div>
                <Label htmlFor="closesAt" className="text-xs text-muted-foreground mb-1.5">
                  Auto-close Date (optional)
                </Label>
                <Input
                  id="closesAt"
                  type="datetime-local"
                  value={closesAt}
                  onChange={(e) => setClosesAt(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2 pb-8 border-t">
          <Button variant="outline" onClick={() => navigate('/a/arche_polls')} disabled={saving}>
            Cancel
          </Button>
          <div className="flex-1" />
          <Button variant="secondary" onClick={() => handleSave(false)} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save as Draft'}
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving}>
            <Send className="h-4 w-4 mr-2" />
            {saving ? 'Publishing...' : 'Publish'}
          </Button>
        </div>
      </div>
    </div>
  );
}
